import { ComposableElement } from '../ComposableElement.mjs';

/**
 * Pill Toggle Web Component
 * 
 * An accessible switch component that utilizes a native hidden checkbox underneath to ensure standard behavior,
 * keyboard accessibility, and standard form submission while rendering a modern pill-shaped UI.
 */
export class PillToggle extends ComposableElement {
    constructor() {
        super();
    }

    // -------------------------------
    // Web Component Lifecycle Methods
    // -------------------------------

    /**
     * Does initial setup and creates the shadow DOM if it wasn't declaratively attached.
     */
    connectedCallback() {
        const internals = this.attachInternals();

        this.shadow = this.shadowRoot;
        if (!this.shadow) {
            this.shadow = this.attachShadow({ mode: 'open' });

            // Defer execution until the browser finishes parsing the children
            setTimeout(() => {
                // Recreate the template using the shadow DOM that is only available through JavaScript
                this.createTemplateInJS(this.shadow);

                // Attach listeners AFTER the input is actually created
                this.#attachEventListeners();
            }, 0);
        }
        else {
            // Shadow DOM already exists, attach event listeners immediately
            this.#attachEventListeners();
        }
    }

    disconnectedCallback() {
        // Cleanup would go here if external event listeners were attached to window/document
    }

    /**
     * Recreate the template in the shadow DOM through JavaScript instead of relying on the `shadowrootmode` attribute
     * 
     * @param {ShadowRoot} shadow The shadow DOM to attach the template to
     */
    createTemplateInJS(shadow) {
        const config = this.initializeComponent('pill-toggle', shadow);

        if(config == null) {
            throw new Error('PillToggle configuration not found. Please ensure you have a script tag with the appropriate data-pill-toggle-config attribute.');
        }

        // Create wrapper label
        const labelWrapper = document.createElement('label');
        labelWrapper.classList.add('toggle-wrapper');
        if (config.disabled) {
            labelWrapper.classList.add('disabled');
        }

        // Create the switch container
        const switchContainer = document.createElement('span');
        switchContainer.classList.add('switch');

        // Create the native checkbox
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('role', 'switch');
        input.id = config.toggleId || 'toggle';
        
        if (config.name) {
            input.name = config.name;
        }

        if (config.checked) {
            input.checked = true;
        }

        if (config.disabled) {
            input.disabled = true;
        }

        // Create the visual track
        const track = document.createElement('span');
        track.classList.add('track');

        // Assemble the switch
        switchContainer.appendChild(input);
        switchContainer.appendChild(track);

        // Create label slot container
        const labelContent = document.createElement('span');
        labelContent.classList.add('label-content');
        
        const labelSlot = document.createElement('slot');
        labelSlot.name = 'toggle-label';
        labelContent.appendChild(labelSlot);

        // Assemble final label
        labelWrapper.appendChild(switchContainer);
        labelWrapper.appendChild(labelContent);

        // Append to shadow DOM
        shadow.appendChild(labelWrapper);
    }

    /**
     * Attach internal event listeners for the component
     * We sync custom element properties to the internal checkbox if necessary.
     */
    #attachEventListeners() {
        // Verify that the shadow DOM is available before attaching event listeners
        if (!this.shadow) {
            return;
        }
        
        // Get the element representing the native checkbox within the shadow DOM
        const input = this.shadow.querySelector('input[type="checkbox"]');
        if (!input) {
            return;
        }

        // Bubble change events out of the shadow DOM to behave like a standard input
        input.addEventListener('change', (e) => {
            this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        });
    }

    // Expose value properties for ease of access programmatically
    get checked() {
        return this.shadow?.querySelector('input')?.checked || false;
    }

    set checked(value) {
        const input = this.shadow?.querySelector('input');
        
        if (input) {
            // !! = Force "truthy-ness"
            input.checked = !!value;
        }
    }
}

/**
 * Dynamically creates a ba-pill-toggle web component.
 * 
 * @param {Object} options - Configuration for the toggle
 * @param {string} options.id - The HTML id for the toggle
 * @param {string} options.name - The form name attribute
 * @param {string} options.label - Text for the toggle label
 * @param {boolean} [options.checked=false] - Initial checked state
 * @param {boolean} [options.disabled=false] - Initial disabled state
 * @param {string} options.componentsStyleHref - Path to the component's CSS
 * @returns {HTMLElement} The constructed ba-pill-toggle element
 */
export function createPillToggle({ 
    id, 
    name, 
    label, 
    checked = false, 
    disabled = false, 
    componentsStyleHref 
}) {
    
    let styleHref = componentsStyleHref;
    if (typeof styleHref !== 'string' || styleHref.length <= 0) {
        // Fallback to the meta tag if not explicitly passed
        const meta = document.querySelector('meta[name="ba-components-style"]');
        
        // If the meta tag is not found, log a warning and return undefined
        if(typeof meta === 'undefined' || meta == null) {
            console.warn('Style not explicitly given AND Meta tag for ba-components-style not found. Returning undefined');
            return undefined;
        }

        // Use the content of the meta tag as the style href
        styleHref = meta.content;
    }

    const toggle = document.createElement('ba-pill-toggle');

    const configScript = document.createElement('script');
    configScript.type = 'application/json';
    configScript.setAttribute('data-pill-toggle-config', '');
    configScript.textContent = JSON.stringify({
        toggleId: id,
        name: name,
        checked: checked,
        disabled: disabled,
        componentsStyleHref: styleHref
    });

    const labelElement = document.createElement('span');
    labelElement.setAttribute('slot', 'toggle-label');
    labelElement.textContent = label;

    toggle.appendChild(configScript);
    toggle.appendChild(labelElement);

    return toggle;
}

document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('ba-pill-toggle')) {
        customElements.define('ba-pill-toggle', PillToggle);
    }
});